import type { FC, ReactElement } from 'react'
import { useState } from 'react'

import type { MessageType } from '@skytech/ui'
import {
  Checkbox,
  Icon,
  Input,
  InputWithValidation,
  Label,
  MessagePopup,
  Radio,
  RadioGroup,
  Tooltip,
} from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { isOnlyDigits, isOnlyLetters, isStringOfLength } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import InfoVariantCircleOutlineIcon from 'mdi-react/InfoVariantCircleOutlineIcon'

import { carrierServicesResources } from '@/api/carrierServices'
import queries from '@/api/queries'
import { subscriptionServicesResources } from '@/api/subscriptionServices'
import type { SubscriptionService } from '@/api/subscriptionServices'
import { Translation } from '@/components/i18n/Translation'
import useSetup from '@/hooks/setup'

import { useTranslation } from '../i18n/hooks'

import './ModifySubscritionConfirmation.styles.scss'

type SimType = 'new' | 'existing' | 'eSim'

export const ModifySubscriptionConfirmation: FC<{
  selectedSub: SubscriptionService
  onSelectSub: (selectedSub: SubscriptionService | null) => void
  onOrderSuccess: () => void
}> = ({ selectedSub, onSelectSub, onOrderSuccess }) => {
  const queryClient = useQueryClient()
  const translate = useTranslation()
  const setup = useSetup()

  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const [simAddress, setSimAddress] = useState('')
  const [simZip, setSimZip] = useState('')
  const [simCity, setSimCity] = useState('')
  const [simType, setSimType] = useState<SimType | undefined>(undefined)
  const [simNumber, setSimNumber] = useState<string>('')
  const [eid, setEid] = useState<string>('')

  const [popupData, setPopupData] = useState<{
    title: string | ReactElement
    message: string | ReactElement
    type: MessageType
  }>({ title: '', message: '', type: 'error' })

  const isAddSubOption = !selectedSub.status

  const carrier = useQuery({
    queryKey: queries.carrierbyid(),
    queryFn: carrierServicesResources.get.carrierById,
    cacheTime: 0,
  })

  const addSubMutation = useMutation(subscriptionServicesResources.addSub, {
    onSuccess() {
      queryClient.invalidateQueries(queries.subscriptionServicesList(), { refetchType: 'all' })
      onOrderSuccess()
    },
    onError() {
      setPopupData({
        type: 'error',
        title: <Translation id="Error of adding a subscription" />,
        message: (
          <Translation id="We have encountered an error while adding this subscription. Please refresh and try again!" />
        ),
      })
    },
  })

  function handleClickSendButton() {
    if (!isTermsAccepted) {
      setPopupData({
        message: <Translation id="Venligst aksepter vilkåre før du sender inn bestilling." />,
        title: <Translation id="Terms not accepted!" />,
        type: 'warning',
      })
      return
    }

    if (isAddSubOption) {
      addSubMutation.mutate({
        serviceId: selectedSub.id,
        address: simAddress,
        city: simCity,
        zip: simZip,
        simCardNo: simNumber || undefined,
        isEsim: simType === 'eSim',
        eid,
        carrierId: carrier.data || undefined,
      })
      return
    }

    throw new Error('This subscription cannot be modified!')
  }

  function resetSimInfo() {
    setSimAddress('')
    setSimCity('')
    setSimZip('')
    setSimNumber('')
  }

  function getIsFormValid() {
    const isAddressValid = simAddress && simCity && isOnlyLetters(simCity) && simZip
    if (!selectedSub.isSimCardProduct) return true
    if (simType === 'new') return isAddressValid
    if (simType === 'existing') return simNumber.length > 0
    if (simType === 'eSim') {
      if (eid) return isOnlyDigits(eid) && isStringOfLength(String(eid), 32)
      return true
    }
    // no type selected
    return false
  }

  const isFormValid = !selectedSub.isSimCardProduct || getIsFormValid()

  return (
    <div className="modify-sub-confirmation">
      <div className="header">
        <Translation
          id={isAddSubOption ? 'Ordering {subName}' : 'Cancelling {subName}'}
          subName={selectedSub.name}
        />
      </div>

      {selectedSub.isSimCardProduct && (
        <RadioGroup
          id="sim-type"
          className="is-new-sim mb-2 gap-1"
          label={<Translation id="Need a sim card?" />}
        >
          <div className="flex flex-col gap-3 md:flex-row">
            <Radio
              id="is-existing-sim"
              checked={simType === 'existing'}
              onChange={() => {
                setSimType('existing')
                resetSimInfo()
              }}
              onClick={() => {
                // To allow deselecting
                if (simType === 'existing') setSimType(undefined)
                resetSimInfo()
              }}
              label={<Translation id="I have a SIM card" />}
            />

            <Radio
              id="is-new-sim"
              checked={simType === 'new'}
              onChange={() => {
                setSimType('new')
                resetSimInfo()
              }}
              onClick={() => {
                // To allow deselecting
                if (simType === 'new') setSimType(undefined)
              }}
              label={<Translation id="I need a new SIM card" />}
            />

            {setup?.allowEsim && selectedSub.isSimCardProduct && (
              <Radio
                id="is-e-sim"
                checked={simType === 'eSim'}
                onChange={() => {
                  setSimType('eSim')
                  resetSimInfo()
                }}
                onClick={() => {
                  // To allow deselecting
                  if (simType === 'eSim') setSimType(undefined)
                }}
                label={<Translation id="I want to activate eSim" />}
              />
            )}
          </div>
        </RadioGroup>
      )}

      {simType === 'eSim' && (
        <div className="mb-5">
          <InputWithValidation
            id="eid-number"
            wrapperClassName="md:ml-3 ml-2"
            marginRight={8}
            className="w-full md:w-1/2"
            label={
              <div className="flex w-full justify-between md:w-1/2">
                <div>
                  <Translation id="EID number" /> ({eid?.length}/32)
                </div>
                {simType === 'eSim' && (
                  <Icon
                    colorMode="info"
                    isWhiteLabeled={false}
                    data-tooltip-id="eid-info"
                    data-tooltip-content={translate('eid-tooltip-info')}
                  >
                    <InfoVariantCircleOutlineIcon />
                  </Icon>
                )}
              </div>
            }
            value={eid || ''}
            validator={val => isOnlyDigits(val) && isStringOfLength(String(val), 32)}
            errorMessage={<Translation id="EID number must consist of 32 digits" />}
            onChange={e => setEid(e.target.value)}
          />
        </div>
      )}

      <Tooltip id="eid-info" size="large" />

      {simType === 'new' && (
        <div className="mt-2 p-2 md:max-w-xl">
          <Label>
            <Translation id="Where should the SIM card be sent?" />
          </Label>

          <Input
            id="new-sub-address"
            isFullWidth
            marginBottom={16}
            label={
              <>
                <Translation id="Address" />*
              </>
            }
            value={simAddress}
            onChange={e => setSimAddress(e.target.value)}
          />

          <div className="flex gap-2">
            <Input
              isFullWidth
              wrapperClassName="shrink-[4] md:shrink"
              marginBottom={16}
              maxLength={10}
              id="zip"
              label={
                <>
                  <Translation id="Zip" />*
                </>
              }
              value={simZip || ''}
              onChange={e => setSimZip(e.target.value)}
            />

            <InputWithValidation
              isFullWidth
              id="city"
              wrapperClassName="grow md:w-full"
              label={
                <>
                  <Translation id="City" />*
                </>
              }
              value={simCity || ''}
              errorMessage={<Translation id="City name may only consist of letters" />}
              validator={isOnlyLetters}
              onChange={e => setSimCity(e.target.value)}
            />
          </div>
        </div>
      )}

      {simType === 'existing' && (
        <div className="mb-4 p-2 md:max-w-xl">
          <InputWithValidation
            id="sim-card-number"
            label={<Translation id="Sim card number" />}
            value={simNumber || ''}
            validator={isOnlyDigits}
            errorMessage={<Translation id="Sim card number may only consist of digits" />}
            onChange={e => setSimNumber(e.target.value)}
          />
        </div>
      )}

      <div className="terms">
        <Translation id="VILLKÅR FOR BESTILLING" />
        <br />
        <br />
        <Translation id="Arbeidstaker gir ved denne aksept arbeidsgiver fullmakt til å foreta lønntrekk for kostnader tilknyttet denne bestillingen. Jf. Arbeidsmiljøloven § 14-15 annet ledd bokstav c." />
      </div>

      <Checkbox
        id="accept-terms"
        marginTop={32}
        label={<Translation id="Kryss av for å akseptere villkår for bestilling" />}
        checked={isTermsAccepted}
        onChange={() => setIsTermsAccepted(prev => !prev)}
      />

      <footer className="footer">
        <Button variant="tertiary" onClick={() => onSelectSub(null)}>
          <Translation id="Cancel" />
        </Button>

        <Button
          variant="primary"
          disabled={!isFormValid}
          isLoading={addSubMutation.isLoading}
          onClick={handleClickSendButton}
        >
          <Translation id="Confirm" />
        </Button>
      </footer>

      <MessagePopup
        message={popupData.message}
        title={popupData.title}
        isOpen={Boolean(popupData.message)}
        type={popupData.type}
        onClose={() => {
          setPopupData({ message: '', title: '', type: 'error' })
        }}
      />
    </div>
  )
}
